import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import { useSurveyParams } from '../context/SurveyParamsContext'
import {
  convertVisibleIf,
  convertTriggers,
  convertQuestionMappings,
  q,
  v,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { userId, sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const userIdRef = useRef(null)
  useEffect(() => {
    if (userId) userIdRef.current = userId
  }, [userId])
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 跳題 小心複選題要用陣列（題號 1-based，工具自動 -1）
  const visibleIfConditionsInit = convertVisibleIf({
    // A5 只在「A3 最常去的場館 ≠ 臺北表演藝術中心」時顯示。
    // A3(q8) 的選項是從 A2(q7) 繼承來的，value 會被重新編號成 0..n-1，
    // 不能用固定索引判斷，故改比對 helpFunction 存下的場館名稱變數 a3Venue。
    q10: "({q8} notempty) and ({a3Venue} <> '臺北表演藝術中心')",

    // B3(q15) 勾「曾參與免費活動」→ B4~B6
    q16: '({q15} anyof [1])',
    q17: '({q15} anyof [1])',
    q18: '({q15} anyof [1])',
    // B3(q15) 勾「曾參與付費活動」→ B7~B9
    q19: '({q15} anyof [2])',
    q20: '({q15} anyof [2])',
    q21: '({q15} anyof [2])',
    // B3 只勾「未參與活動」(互斥 none) → q16~q21 全不顯示，直接到 B10(q22)

    // B10(q22) 勾 6)網紅分享 或 13)創作者平台 → B10-1
    q23: '({q22} anyof [6, 13])',
    // B10(q22) 勾 7)藝文專門媒體 → B10-2
    q24: '({q22} anyof [7])',

    // B11(q25) 有加入官方LINE → B12、B13；未加入 → B14
    q26: '({q25} = 1)',
    q27: '({q25} = 1)',
    q28: '({q25} = 2)',

    // C1(q29) 會員身分分流
    q30: '({q29} = 1)', // C2 免費會員原因
    q31: '({q29} = 1)', // C3 沒成為付費會員原因
    q32: '({q29} = 2)', // C4 沒續會原因
    q33: '({q29} = 3)', // C5 成為付費會員原因
    q34: '({q29} = 4)', // C6 沒註冊會員原因
    // C7 排序題(q35) 與其承接的「其他說明」文字題(q36)。
    // 排序題的「其他，請說明：」是第 10 項且 o_type=0（非半開放），
    // 故用數字索引 10 判斷有沒有被排進前三名。
    q35: '({q29} = 1) or ({q29} = 2) or ({q29} = 4)',
    q36: '(({q29} = 1) or ({q29} = 2) or ({q29} = 4)) and ({q35} anyof [10])',
    // C8 排序題(q37) 與其承接的「其他說明」文字題(q38)
    q37: '({q29} = 2) or ({q29} = 3)',
    q38: '(({q29} = 2) or ({q29} = 3)) and ({q37} anyof [10])',

    // D1(q40) 有購買過北藝節目 → D2~D5
    q41: '({q40} = 1)',
    q42: '({q40} = 1)',
    q43: '({q40} = 1)',
    q44: '({q40} = 1)',
    // D5(q44) 選 1)會優先選擇北藝主辦節目 → D6；選 2~4 直接跳到 D7(q46)
    q45: '({q40} = 1) and ({q44} = 1)',
    // D7(q46)、D8(q47) 兩條路徑都會走到，不設條件
  })

  // 非必填(從 0 計算)
  const notRequired = null

  const mappedRateChoice = null
  const pageBreakQuestions = null

  // 篩出：S1 不同意 / S3 年齡 19歲以下或65歲以上 / S4 以上皆無
  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression:
        `({q1} = 2) or ` +
        `({q3} = 1) or ({q3} = 11) or ` +
        `({q4} anyof ["none"])`,
    },
  ])

  // 帶出: 帶入 (用1開始計算填這裡)
  const questionMappings = convertQuestionMappings({
    q4: 'q5', // S4 購票觀賞過的類型（複選）→ S5 最常觀賞的類型（單選）
    q7: 'q8', // A2 購票觀賞過的場館（複選）→ A3 最常購票的場館（單選）
    q41: 'q42', // D2 北藝買過的節目類型（複選）→ D3 最常買的類型（單選）
  })

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    sfdSamplerId,
    sfdPId,
    setWebAuthType,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    sfdSamplerId,
    webAuthType,
  })

  useEffect(() => {
    if (surveyModel) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [surveyModel])

  // 當 surveyModel 建立時，自動捲動到頂端
  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  // A3(q8) 的選項繼承自 A2(q7)，選項 value 會被重編成 0..n-1，
  // 固定索引無法判斷「最常去的是不是臺北表演藝術中心」。
  // 這裡把選到的場館「文字」存成 survey 變數 a3Venue，給 q10(A5) 的 visibleIf 用。
  function helpFunction(survey, options) {
    if (options?.name !== q(8)) return
    const choice = options.question?.choices?.find(
      (c) => c.value === options.value
    )
    survey.setVariable('a3Venue', choice ? choice.text : '')
  }

  // 篩出（不符資格 / 樣本額滿）文案
  const endMessage = `<div>不好意思，恰好與您相同特性的樣本已額滿，本次無法繼續作答。未來若有其他經驗調查，再邀請您參與，謝謝您的時間。<br><br>如有任何問題，請於週一到週五上班日10:00~18:00電話聯繫臺北表演藝術中心或典通，我們會盡快回覆您的問題。<br><br>北藝專案窗口│02-77563800 #1335<br>典通市調專線│02-23315133 #827</div>`

  // 正常完成結語
  const completedMessage = `<div>感謝您與我們分享想法！<br><br>如有任何問題，請於週一到週五上班日10:00~18:00電話聯繫臺北表演藝術中心或典通，我們會盡快回覆您的問題。<br><br>北藝專案窗口│02-77563800 #1335<br>典通市調專線│02-23315133 #827</div>`

  const endSurveyMsg = (survey) => {
    if (
      survey.getValue(q(1)) === v(2) ||
      survey.getValue(q(3)) === v(1) ||
      survey.getValue(q(3)) === v(11) ||
      survey.getValue(q(4))?.[0] === 'none'
    ) {
      setCompletionMessage(endMessage)
    } else {
      setCompletionMessage(completedMessage)
    }
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    userIdRef,
    enableProgressSaving: webAuthType > 0 || !!sfdSamplerId,
    sfdSamplerId,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
